/* Clean Otter — global shell JS (vanilla, no dependencies)
   Drawers, sticky header, cart AJAX, card-processing-fee sync. */
(function () {
  'use strict';
  const CO = window.CleanOtter || {};

  /* ---------- drawers / overlays ---------- */
  const scrim = document.getElementById('scrim');
  let openEl = null;

  function open(id) {
    close();
    openEl = document.getElementById(id);
    if (!openEl) return;
    openEl.classList.add('is-open');
    scrim && scrim.classList.add('is-on');
    document.documentElement.classList.add('no-scroll');
    const input = openEl.querySelector('input[type="search"]');
    input && setTimeout(() => input.focus(), 250);
  }
  function close() {
    document.querySelectorAll('.is-open').forEach((e) => e.classList.remove('is-open'));
    scrim && scrim.classList.remove('is-on');
    document.documentElement.classList.remove('no-scroll');
    openEl = null;
  }
  /* Exposed so components.js (wishlist) can open/close a drawer the same way
     the add-to-cart flow does, without duplicating this logic. */
  CO.openDrawer = open;
  CO.closeDrawer = close;
  window.CleanOtter = CO;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('.js-open');
    if (opener) { e.preventDefault(); open(opener.dataset.target); return; }
    if (e.target.closest('.js-close')) { e.preventDefault(); close(); return; }
    if (e.target.closest('.js-dismiss-announce')) {
      const a = document.getElementById('announce');
      a && a.remove();
      try { sessionStorage.setItem('co-announce-dismissed', '1'); } catch (_) {}
    }
  });
  scrim && scrim.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  try {
    if (sessionStorage.getItem('co-announce-dismissed')) {
      const a = document.getElementById('announce');
      a && a.remove();
    }
  } catch (_) {}

  /* ---------- sticky header shadow ---------- */
  const hdr = document.getElementById('hdr');
  if (hdr) {
    addEventListener('scroll', () => hdr.classList.toggle('is-scrolled', scrollY > 8), { passive: true });
    /* Publish the real header height so sticky sub-navs and anchor scrolling
       stop underneath it rather than behind it. */
    const setHdrHeight = () =>
      document.documentElement.style.setProperty('--h-hdr', hdr.offsetHeight + 'px');
    setHdrHeight();
    if (window.ResizeObserver) new ResizeObserver(setHdrHeight).observe(hdr);
    else addEventListener('resize', setHdrHeight, { passive: true });
  }

  /* ---------- cart ---------- */
  async function cartRequest(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }
  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /* ---------- card processing fee ----------------------------------------
     The buyer covers the card processing charge, quoted as a percentage of the
     basket (2.95% by default). A Shopify basket cannot hold a percentage line,
     so the fee rides in it as a fee product priced at one minor unit (€0.01)
     whose QUANTITY is the fee in cents: €100 of goods -> 295 × €0.01 = €2.95.

     Everything below is integer cent maths — the rate arrives as basis points
     (2.95% -> 295) so no float rounding can leave the basket a cent out.

     This is the UX layer: it keeps the fee line honest as the basket changes
     and renders it as a named row in the totals. The Cart & Checkout Validation
     Function is the enforcement layer that refuses checkout on a wrong fee. */
  const FEE = {
    id: parseInt(CO.feeVariantId, 10) || 0,
    bp: parseInt(CO.feeBasisPoints, 10) || 0,
    unit: parseInt(CO.feeUnitPrice, 10) || 0,
    /* absent (older settings_data) counts as on — the fee is the default state */
    on: CO.feeEnabled !== false,
    /* flipped once a fee line has been seen priced at exactly FEE.unit */
    verified: false,
  };
  const feeActive = () => FEE.on && FEE.id > 0 && FEE.bp > 0 && FEE.unit > 0;

  /* Split a basket into the goods it is charging for and the fee riding on it.
     feeUnit is what the fee variant is ACTUALLY charging per unit, which the
     interlock in syncFee() checks against what the theme is configured for. */
  function readCart(cart) {
    const feeLines = [];
    let feeTotal = 0;
    let units = 0;
    (cart.items || []).forEach((item) => {
      if (FEE.id && item.variant_id === FEE.id) {
        feeLines.push(item);
        feeTotal += item.final_line_price;
        units += item.quantity;
      }
    });
    /* Goods = the basket total less the fee riding in it, which is exactly how
       snippets/cart-totals.liquid derives the figure it prints. Summing the
       non-fee line prices instead looks equivalent and is not: cart.total_price
       is net of cart-level discounts and the line prices are not, so the two
       would quote different fees the moment a discount is applied. */
    const total = typeof cart.total_price === 'number'
      ? cart.total_price
      : (cart.items || []).reduce((n, i) => n + i.final_line_price, 0);
    const base = Math.max(0, total - feeTotal);
    return { feeLines, units, base, feeUnit: feeLines.length ? feeLines[0].final_price : null };
  }

  function feeMisconfigured(observed) {
    console.warn(
      `[Clean Otter] Fee variant ${FEE.id} charges ${observed} per unit, but Checkout fee > ` +
      `"Fee unit price" is set to ${FEE.unit}. The fee is carried by the line's QUANTITY, so ` +
      `the wrong unit price would overcharge — fee removed. Point the setting at the ` +
      `${FEE.unit}-cent fee variant.`
    );
  }

  /* Units of the fee product a given goods subtotal is worth.
     base × bp / 10000 is the fee in cents, rounded half up. */
  function feeUnitsFor(base) {
    if (!feeActive() || base <= 0) return 0;
    const cents = Math.round((base * FEE.bp) / 10000);
    return cents > 0 ? Math.ceil(cents / FEE.unit) : 0;
  }

  /* Bring the fee line into step with the basket. Returns the settled cart.
     Gated on the variant, not on feeActive(): if the fee is switched off,
     feeUnitsFor() returns 0 and this clears fee lines left in live baskets. */
  async function syncFee(cart) {
    if (!FEE.id) return cart;
    const { feeLines, units, base, feeUnit } = readCart(cart);

    /* Interlock. The line's QUANTITY is what carries the fee, so the variant has
       to be priced at exactly fee_unit_price. Aim the setting at the wrong
       product — the retired €0.99 platform fee is the obvious trap — and the
       same maths bills 295 × €0.99. Strip the line and stop rather than charge
       it; the basket then shows no fee at all, which is loud enough to notice. */
    if (feeUnit !== null && feeUnit !== FEE.unit) {
      feeMisconfigured(feeUnit);
      try {
        for (const line of feeLines) {
          await cartRequest(CO.routes.cartChange, { id: line.key, quantity: 0 });
        }
        return getCart();
      } catch (_) { return cart; }
    }
    if (feeUnit !== null) FEE.verified = true;

    const want = feeUnitsFor(base);
    if (want === units && feeLines.length < 2) return cart;
    try {
      if (feeLines.length > 1) {
        /* shouldn't happen — Shopify merges identical lines — but never leave
           the basket carrying the fee twice */
        for (const line of feeLines) {
          await cartRequest(CO.routes.cartChange, { id: line.key, quantity: 0 });
        }
        if (want > 0) await cartRequest(CO.routes.cartAdd, { items: [{ id: FEE.id, quantity: want }] });
      } else if (feeLines.length === 1) {
        await cartRequest(CO.routes.cartChange, { id: feeLines[0].key, quantity: want });
      } else if (want > 0) {
        /* No line to price-check against yet, and adding `want` units outright
           would spend the overcharge before we could look. Probe with one unit,
           confirm, then multiply up. */
        const added = await cartRequest(CO.routes.cartAdd, { items: [{ id: FEE.id, quantity: 1 }] });
        /* newer cart/add.js answers { items: [...] }; older ones the line itself */
        const addedLines = added.items || (added.variant_id ? [added] : []);
        const line = addedLines.find((i) => i.variant_id === FEE.id);
        if (line && line.final_price !== FEE.unit) {
          feeMisconfigured(line.final_price);
          await cartRequest(CO.routes.cartChange, { id: String(FEE.id), quantity: 0 });
          return getCart();
        }
        FEE.verified = true;
        if (want > 1) await cartRequest(CO.routes.cartChange, { id: String(FEE.id), quantity: want });
      }
      return getCart();
    } catch (err) {
      /* Checkout is still protected by the validation function, but say why —
         a product id pasted into Fee variant ID is the classic cause, and
         cart/add.js rejecting it is otherwise completely silent. */
      console.warn(
        `[Clean Otter] Could not settle the card processing fee with variant ${FEE.id}: ` +
        `${err.message}. Check that Checkout fee > "Fee variant ID" holds a VARIANT id ` +
        `(not a product id) — /products/<handle>.js lists it under variants[0].id.`
      );
      return cart;
    }
  }

  /* ---------- rendering ---------- */
  /* Re-render a section in place via the Section Rendering API. */
  async function renderSection(id, selector) {
    try {
      const res = await fetch(`${location.pathname}?sections=${encodeURIComponent(id)}`);
      const json = await res.json();
      const html = json[id];
      if (!html) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const fresh = tmp.querySelector(selector);
      const current = document.querySelector(selector);
      if (!fresh || !current) return;
      const wasOpen = current.classList.contains('is-open');
      current.replaceWith(fresh);
      if (wasOpen) fresh.classList.add('is-open');
    } catch (_) {}
  }

  /* The drawer always; the cart page too when that is what the buyer is on, so
     the fee row and order total there never lag the basket. */
  async function refreshCartUI() {
    await renderSection('cart-drawer', '#cart-drawer');
    const page = document.querySelector('[data-cart-section]');
    if (page) await renderSection(page.dataset.cartSection, '[data-cart-section]');
  }

  function updateCounts(cart) {
    const visible = (cart.items || [])
      .filter((i) => i.variant_id !== FEE.id)
      .reduce((n, i) => n + i.quantity, 0);
    document.querySelectorAll('#cart-count, #drawer-count').forEach((el) => {
      el.textContent = visible;
      el.classList.toggle('is-empty', visible === 0);
    });
  }

  async function afterCartChange() {
    let cart = await getCart();
    cart = await syncFee(cart);
    updateCounts(cart);
    await refreshCartUI();
    return cart;
  }

  /* add to basket (cards + product page) */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.js-add-to-cart');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-busy');
    btn.classList.remove('is-error');
    /* PDP has a live quantity input (respects the qty stepper); cards fall
       back to the button's own data-qty since they have no form/input. */
    const qtyInput = btn.closest('form')?.querySelector('input[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : parseInt(btn.dataset.qty, 10) || 1;
    const errBox = btn.closest('.pricecard')?.querySelector('[data-add-error]');
    if (errBox) errBox.hidden = true;
    try {
      const items = [{ id: parseInt(btn.dataset.variantId, 10), quantity: qty }];
      /* Top the fee up in the SAME request as the item so the basket is never
         momentarily short of it — the validation function rejects that state.
         data-price is the list price, so volume breaks and B2B catalogue prices
         can leave this a few cents out; the syncFee() inside afterCartChange()
         settles the exact amount immediately after. Skipped until syncFee has
         vouched for the fee variant's unit price — the very first add of a
         session therefore picks the fee up one request later. */
      if (feeActive() && FEE.verified) {
        const cart = await getCart();
        const { base, units } = readCart(cart);
        const each = parseInt(btn.dataset.price, 10) || 0;
        const delta = feeUnitsFor(base + each * qty) - units;
        if (delta > 0) items.push({ id: FEE.id, quantity: delta });
      }
      await cartRequest(CO.routes.cartAdd, { items });
      await afterCartChange();
      open('cart-drawer');
    } catch (err) {
      btn.classList.add('is-error');
      if (errBox) { errBox.textContent = err.message; errBox.hidden = false; }
    } finally {
      btn.classList.remove('is-busy');
    }
  });

  /* qty steppers in drawer (respect quantity-rule increments via data-delta) */
  document.addEventListener('click', async (e) => {
    const q = e.target.closest('.js-qty');
    if (!q) return;
    e.preventDefault();
    const line = parseInt(q.dataset.line, 10);
    const delta = parseInt(q.dataset.delta, 10);
    const span = q.parentElement && q.parentElement.querySelector('span');
    if (!span) return;
    const next = Math.max(0, parseInt(span.textContent, 10) + delta);
    try {
      await cartRequest(CO.routes.cartChange, { line, quantity: next });
      await afterCartChange();
    } catch (_) {}
  });

  /* ---------- clear basket ----------
     Two-step rather than a native confirm(): the first click arms the link, the
     second empties the basket, and anything else — another click, four seconds,
     closing the drawer — disarms it. Emptying a built wholesale order by
     mis-tap is the failure worth designing against; a modal is not this
     theme's idiom.
     /cart/clear.js takes the fee line with it, so the afterCartChange() that
     follows settles against an empty basket and syncFee() has nothing to do. */
  const CLEAR_ARM_MS = 4000;
  let clearTimer = null;

  function disarmClear() {
    clearTimeout(clearTimer);
    clearTimer = null;
    document.querySelectorAll('.js-cart-clear.is-confirm').forEach((b) => {
      b.classList.remove('is-confirm');
      if (b.dataset.label) b.textContent = b.dataset.label;
    });
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.js-cart-clear');
    /* any other click puts an armed button back to rest */
    if (!btn) { if (clearTimer) disarmClear(); return; }
    e.preventDefault();

    if (!btn.classList.contains('is-confirm')) {
      if (!btn.dataset.label) btn.dataset.label = btn.textContent.trim();
      btn.classList.add('is-confirm');
      btn.textContent = btn.dataset.confirmLabel || btn.dataset.label;
      clearTimeout(clearTimer);
      clearTimer = setTimeout(disarmClear, CLEAR_ARM_MS);
      return;
    }

    clearTimeout(clearTimer);
    clearTimer = null;
    btn.classList.add('is-busy');
    try {
      /* Fall back on the cart route if the layout predates the cartClear entry
         — a theme file can be live without theme.liquid having caught up. */
      const url = CO.routes.cartClear || `${CO.routes.cartUrl || '/cart'}/clear.js`;
      await cartRequest(url, {});
      /* re-renders the drawer, so the armed button goes with it */
      await afterCartChange();
    } catch (_) {
      btn.classList.remove('is-busy');
      disarmClear();
    }
  });

  /* Escape closes the drawer without a click, so it needs its own disarm. */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && clearTimer) disarmClear();
  });

  /* Cart page: update / checkout.
     Taken over from the plain form post so the fee is recalculated against the
     new quantities before the buyer sees a total — or reaches checkout with a
     stale one. Keyed updates (line item keys) leave the fee line alone; syncFee
     resettles it straight after. The form still works without JS. */
  /* SubmitEvent.submitter tells update from checkout; keep a fallback so an old
     browser can never swallow a checkout click and silently just update. */
  let lastSubmitter = null;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('button[type="submit"], input[type="submit"]');
    if (b) lastSubmitter = b;
  }, true);

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.cartform');
    if (!form) return;
    e.preventDefault();
    const submitter = e.submitter || (lastSubmitter && form.contains(lastSubmitter) ? lastSubmitter : null);
    const toCheckout = submitter && submitter.name === 'checkout';
    const updates = {};
    form.querySelectorAll('[data-line-key]').forEach((input) => {
      updates[input.dataset.lineKey] = parseInt(input.value, 10) || 0;
    });
    submitter && submitter.classList.add('is-busy');
    try {
      if (Object.keys(updates).length) await cartRequest(CO.routes.cartUpdate, { updates });
      if (toCheckout) await syncFee(await getCart());
      else await afterCartChange();
    } catch (_) {
    } finally {
      submitter && submitter.classList.remove('is-busy');
    }
    if (toCheckout) location.href = `${CO.routes.cartUrl || '/cart'}/checkout`;
  });

  /* Drawer checkout: settle the fee against the live basket before handing the
     buyer over to Shopify's checkout. */
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-checkout]');
    if (!link) return;
    e.preventDefault();
    const href = link.getAttribute('href');
    link.classList.add('is-busy');
    try { await syncFee(await getCart()); } catch (_) {}
    location.href = href;
  });

  /* Quick order posts its own multi-line add and announces it. */
  document.addEventListener('cart:changed', () => { afterCartChange().catch(() => {}); });

  /* Settle the fee on load — the basket may have been changed in another tab,
     by a Shopify-side edit, or by a no-JS form post. Only repaint if it moved. */
  if (FEE.id) {
    getCart()
      .then(async (cart) => {
        const before = readCart(cart).units;
        const settled = await syncFee(cart);
        updateCounts(settled);
        if (readCart(settled).units !== before) await refreshCartUI();
      })
      .catch(() => {});
  }
})();
